import styled from '@emotion/styled';
import { Modal, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { theme } from 'components/themes/v1';
import { DateToShortDateTime } from 'utils/datetime';

const NotificationModalTable = styled(Table)`
  font-size: 0.85rem;
  th {
    background-color: ${(props) => props.theme.colors.gray100};
    font-weight: 600;
  }
`;

const NotificationBadgeSmall = styled.span`
  display: inline-block;
  background-color: ${theme.colors.danger};
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.primary};
  }
`;

const ModalPagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  padding: 0;
  
  button {
    padding: 2px 6px;
    border: 1px solid ${(props) => props.theme.colors.divider};
    background: white;
    cursor: pointer;
    border-radius: 4px;
    font-size: 0.85rem;
    
    &:hover {
      background: ${(props) => props.theme.colors.gray100};
    }
    
    &.active {
      background: ${theme.colors.primary};
      color: white;
      border-color: ${theme.colors.primary};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const ModalLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const NotificationModal = ({
  show,
  onHide,
  notifications,
  modalPage,
  modalTotalPages,
  modalLoading,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onClose,
  onMarkAllAsRead,
  notifUnread,
  hoveredUnreadId,
  onHoveredUnreadIdChange,
  onMarkAsRead,
  getPageNumbersToShow,
  language,
  minStatusWidth = '200px'
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Modal show={show} onHide={onHide} size='lg' centered backdrop='static' keyboard={false}>
      <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} closeButton={false}>
        <Modal.Title style={{ flex: 1 }}>{t('menu.notifications')}</Modal.Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ margin: 0, fontSize: '0.9rem' }}>{t('per_page')}:</label>
          <select 
            value={itemsPerPage} 
            onChange={onItemsPerPageChange}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: `1px solid ${theme.colors.divider}`,
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}>
        {modalLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.85rem',
            color: '#666',
            zIndex: 10
          }}>
            {t('loading')}
          </div>
        )}
        
        {notifications && notifications.length > 0 ? (
          <NotificationModalTable striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{ textTransform: 'capitalize' }}>{t('content') || 'Content'}</th>
                <th style={{ textTransform: 'capitalize' }}>{t('date_label') || 'Date'}</th>
                <th style={{ textTransform: 'capitalize', minWidth: minStatusWidth }}>{t('status') || 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notif, idx) => (
                <tr key={notif.id} style={{ cursor: 'pointer' }} onClick={() => onMarkAsRead(notif)}>
                  <td>{notif.content}</td>
                  <td>{DateToShortDateTime(notif.created_at, language || i18n.language)}</td>
                  <td>
                    {!notif.is_read ? (
                      <NotificationBadgeSmall 
                        onMouseEnter={() => onHoveredUnreadIdChange(notif.id)}
                        onMouseLeave={() => onHoveredUnreadIdChange(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkAsRead(notif);
                        }}
                      >
                        {hoveredUnreadId === notif.id ? t('mark_as_read') || 'Mark as Read' : t('unread') || 'Unread'}
                      </NotificationBadgeSmall>
                    ) : (
                      <span>{t('read') || 'Read'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </NotificationModalTable>
        ) : modalLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>{t('loading')}</div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>{t('notification_empty') || 'No notifications'}</div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {modalTotalPages > 1 && (
          <ModalPagination>
            <button
              disabled={modalPage === 1 || modalLoading}
              onClick={() => onPageChange(1)}
              title='First page'
            >
              «
            </button>
            
            <button
              disabled={modalPage === 1 || modalLoading}
              onClick={() => onPageChange(modalPage - 1)}
            >
              ‹ Prev
            </button>
            
            {getPageNumbersToShow().map((page) => (
              <button
                key={page}
                className={modalPage === page ? 'active' : ''}
                onClick={() => onPageChange(page)}
                disabled={modalLoading}
              >
                {page}
              </button>
            ))}
            
            <button
              disabled={modalPage === modalTotalPages || modalLoading}
              onClick={() => onPageChange(modalPage + 1)}
            >
              Next ›
            </button>
            
            <button
              disabled={modalPage === modalTotalPages || modalLoading}
              onClick={() => onPageChange(modalTotalPages)}
              title='Last page'
            >
              »
            </button>
          </ModalPagination>
        )}
        <div style={{ display: 'flex', gap: '5px' }}>
          {notifUnread > 0 && (
            <Button 
              variant='primary' 
              size='sm'
              onClick={onMarkAllAsRead}
              disabled={modalLoading}
            >
              {t('mark_all_as_read') || 'Mark All as Read'}
            </Button>
          )}
          <Button variant='secondary' size='sm' onClick={onClose}>
            {t('close') || 'Close'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default NotificationModal;
